import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Table, TooltipCustom, ReactNotifications } from '../../components';
import { DATE_YMD, STUDENT_STATUS, STUDENT_STATUS_STRING } from '../../core/configs/constants';
import { useStore } from '../../core/utils/hook';
import moment from 'moment';
import ApprovalConfirmModal from './components/ApprovalConfirmModal';
import './components/ApprovalConfirmModal.scss';

// GA screen for the 承認待ち count: shows ONLY pending-approval learners of the attendance code,
// with approve (✓) / reject (✗) actions. Same table UI as StudentListByAttendanceCodeScreen.
const PendingApprovalStudentListScreen = observer(() => {

    const { id } = useParams();

    const {
        groupAdminStore: { getStudentListByAttendanceCode, studentListByAttendanceCode, getApprovalAttendanceCodes, approveStudent, rejectStudent },
        userStore: { userInfoHeader }
    } = useStore();

    // Confirmation modal: mode 'approve' | 'reject' | null + the target student.
    const [modalMode, setModalMode] = useState(null);
    const [targetStudent, setTargetStudent] = useState(null);
    // 受講コード options of the approve modal, reloaded each time it opens (slots/expiry may have changed).
    const [attendanceCodes, setAttendanceCodes] = useState([]);

    // Group has the approval service enabled AND this GA has the approval permission.
    const canApprove = userInfoHeader?.isAllowApproveAttendanceCode === true;

    useEffect(() => {
        getStudentListByAttendanceCode(id, { scope: 'pending' });
    }, [id])

    // BE returns the pending (non-resigned) subset; FE only sorts newest-first (作成日).
    const sortedData = useMemo(
        () => (studentListByAttendanceCode?.listUser || [])
            .slice()
            .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()),
        [studentListByAttendanceCode?.listUser]
    );

    const closeModal = () => {
        setModalMode(null);
        setTargetStudent(null);
        setAttendanceCodes([]);
    };

    const openApproveModal = async (student) => {
        const codes = await getApprovalAttendanceCodes(student.id);
        // Request failed: ApiStore has already shown the error, keep the modal closed.
        if (!codes) return;
        setAttendanceCodes(codes);
        setTargetStudent(student);
        setModalMode('approve');
    };

    const handleApprove = async (payload) => {
        if (!targetStudent) return;
        const result = await approveStudent(targetStudent.id, payload);
        if (result === true) {
            closeModal();
            ReactNotifications('success', '受講者を承認しました。');
            getStudentListByAttendanceCode(id, { scope: 'pending' });
            return;
        }
        // The request itself failed (network / 5xx / session): its error is already on screen — leave the
        // modal untouched for a retry and fire no further request (each failure would stack one more modal).
        if (!result) return;
        // Refused by the BE (ApiStore has already shown why, e.g. "pick another 受講コード"): keep the modal
        // open on a fresh code list so the GA can choose again. If the learner can no longer be approved
        // at all (already handled by someone else), close and reload the table instead.
        const codes = await getApprovalAttendanceCodes(targetStudent.id, true);
        if (codes) {
            setAttendanceCodes(codes);
        } else {
            closeModal();
            getStudentListByAttendanceCode(id, { scope: 'pending' });
        }
    };

    const handleReject = async () => {
        if (!targetStudent) return;
        const result = await rejectStudent(targetStudent.id, {});
        closeModal();
        if (result === true) {
            ReactNotifications('success', '不承認としました。個人受講者へ変更しました。');
        }
        // Reload on success and also when the BE refused (e.g. the learner was just approved by someone
        // else), so a stale row does not stay in the table. Not when the request itself failed.
        if (result) {
            getStudentListByAttendanceCode(id, { scope: 'pending' });
        }
    };

    const columns = [
        {
            Header: 'ID',
            accessor: 'userId',
            disableSortBy: true,
            Cell: ({ row: { original } }) => <div>{original?.userId}</div>,
            width: '10%'
        },
        {
            Header: '名前',
            accessor: 'name',
            disableSortBy: true,
            Cell: ({ row: { original } }) => (
                <div>
                    {original?.inGroup
                        ? <Link to={`/student-detail/${original.id}`}>{original?.name}</Link>
                        : original?.name}
                </div>
            ),
            width: '20%'
        },
        {
            // Every row here is pending on THIS SAME attendance code, so the ticket plan name is a
            // single value for the whole table, not per-student — read straight off the top-level response.
            Header: '受講コース',
            accessor: 'ticketName',
            disableSortBy: true,
            Cell: () => <div>{studentListByAttendanceCode?.ticketName}</div>,
            width: '15%'
        },
        {
            Header: 'メールアドレス',
            accessor: 'email',
            disableSortBy: true,
            Cell: ({ row: { original } }) => <div>{original?.email}</div>,
            width: '25%'
        },
        {
            Header: 'ステータス',
            accessor: 'status',
            disableSortBy: true,
            // 承認待ち shown in orange per Figma
            Cell: () => <div style={{ color: '#ffa600' }}>{STUDENT_STATUS_STRING.PENDING_APPROVAL}</div>,
            width: '10%'
        },
        {
            Header: '受講開始日',
            accessor: 'course_period',
            disableSortBy: true,
            className: 'min-width-150',
            Cell: ({ row: { original } }) => (
                <div className='approval-course-period-cell'>
                    {original.coursePeriod ? moment(original.coursePeriod).format(DATE_YMD) : ''}
                </div>
            ),
            width: '10%'
        },
        {
            Header: 'アクション',
            accessor: 'action',
            disableSortBy: true,
            Cell: ({ row: { original } }) => {
                // Show 承認/不承認 only when ALL hold:
                //  (1) student is 承認待ち and belongs to the GA's group (inGroup)
                //  (2) group has the approval service + (3) GA has the approval permission → canApprove
                const showActions = canApprove &&
                    original?.status === STUDENT_STATUS.PENDING_APPROVAL &&
                    original?.inGroup;
                if (!showActions) {
                    return <div></div>;
                }
                return (
                    <div className='d-flex flex-gap-10 justify-content-center approval-action-cell'>
                        <TooltipCustom title='承認'>
                            <button
                                type='button'
                                className='approval-action-btn approval-action-btn--approve'
                                onClick={() => openApproveModal(original)}
                            >
                                <i className='fa-solid fa-check'></i>
                            </button>
                        </TooltipCustom>
                        <TooltipCustom title='不承認'>
                            <button
                                type='button'
                                className='approval-action-btn approval-action-btn--reject'
                                onClick={() => { setTargetStudent(original); setModalMode('reject'); }}
                            >
                                <i className='fa-solid fa-xmark'></i>
                            </button>
                        </TooltipCustom>
                    </div>
                );
            },
            width: '10%'
        }
    ];

    return (
        <div id='main' className='wrapper main-student-list main-view-student-detail main-student-list'>
            <section className='content-student-list'>
                <div className='container'>
                    <h1 className='title-primary'>受講コード #{studentListByAttendanceCode?.attendanceCode} 別の受講者リスト</h1>
                    <Table
                        columns={columns}
                        data={sortedData}
                        disablePaging={true}
                        scrollHorizontalWidth={'768px'}
                        className='lst-attendance-table mg-t-35'
                    />
                </div>
            </section>

            <ApprovalConfirmModal
                mode={modalMode}
                student={targetStudent}
                attendanceCodes={attendanceCodes}
                onCancel={closeModal}
                onApprove={handleApprove}
                onReject={handleReject}
            />
        </div>
    );
});

export default PendingApprovalStudentListScreen;
